import { inject, Injectable } from '@angular/core';
import {
  addDoc,
  collection,
  collectionGroup,
  deleteDoc,
  doc,
  Firestore,
  getDoc,
  getDocs,
  serverTimestamp,
  setDoc,
  query,
  updateDoc,
  where
} from '@angular/fire/firestore';
import { from, map, Observable } from 'rxjs';

export type SchemaColumn = {
  name: string;
  label: string;
  data_type: 'text' | 'integer' | 'decimal' | 'boolean' | 'date' | 'timestamp' | 'json' | string;
  required: boolean;
  unique?: boolean;
  default_value?: string;
  description?: string;
  example?: string;
  max_length?: number;
  enum_values?: string[];
  regex?: string;
  min_value?: number;
  max_value?: number;
  precision?: number;
  scale?: number;
  date_format?: string;
};

export type SchemaDestination = {
  type: 'postgres' | 'bigquery' | 'mysql' | 'snowflake' | string;
  target: {
    table: string;
    schema: string;
    project?: string;
  };
};

export type DatasetSchema = {
  id: string;
  name: string;
  description?: string;
  uid?: string;
  write_mode?: 'replace' | 'update' | 'insert' | 'replace_scope';
  replace_scope?: Record<string, unknown> | null;
  destinations?: SchemaDestination[];
  columns: SchemaColumn[];
  rules?: unknown[];
  created_at?: unknown;
  updated_at?: unknown;
  type?: string;
  data_source?: string;
  schema?: SchemaColumn[];
};

export type SchemaPermission = {
  uid: string;
  schema_id: string;
  [key: string]: unknown;
};

@Injectable({ providedIn: 'root' })
export class SchemasService {
  private firestore = inject(Firestore);

  createSchema$(payload: Omit<DatasetSchema, 'id'>): Observable<string> {
    const schemasRef = collection(this.firestore, 'schemas');
    return from(
      addDoc(schemasRef, {
        ...payload,
        created_at: serverTimestamp(),
        updated_at: serverTimestamp()
      })
    ).pipe(map((docRef) => docRef.id));
  }

  updateSchema$(schemaId: string, payload: Partial<DatasetSchema>): Observable<void> {
    const schemaRef = doc(this.firestore, 'schemas', schemaId);
    return from(
      updateDoc(schemaRef, {
        ...payload,
        id: schemaId,
        updated_at: serverTimestamp()
      })
    );
  }

  getSchemaById$(schemaId: string): Observable<DatasetSchema | null> {
    const schemaRef = doc(this.firestore, 'schemas', schemaId);
    return from(getDoc(schemaRef)).pipe(
      map((snap) => {
        if (!snap.exists()) return null;
        return {
          id: snap.id,
          ...(snap.data() as Omit<DatasetSchema, 'id'>)
        };
      })
    );
  }

  deleteSchema$(schemaId: string): Observable<void> {
    const schemaRef = doc(this.firestore, 'schemas', schemaId);
    return from(deleteDoc(schemaRef));
  }

  getSchemaPermissions$(schemaId: string): Observable<SchemaPermission[]> {
    const permissionsRef = collection(this.firestore, `schemas/${schemaId}/permissions`);
    return from(getDocs(permissionsRef)).pipe(
      map((snap) =>
        snap.docs.map((d) => {
          const raw = d.data() as Record<string, unknown>;
          return {
            uid: d.id,
            schema_id: String(raw['schema_id'] ?? schemaId)
          };
        })
      )
    );
  }

  upsertSchemaPermission$(schemaId: string, permission: SchemaPermission): Observable<void> {
    const permissionRef = doc(this.firestore, `schemas/${schemaId}/permissions/${permission.uid}`);
    return from(
      setDoc(
        permissionRef,
        {
          ...permission,
          uid: permission.uid,
          schema_id: schemaId,
          created_at: serverTimestamp(),
          updated_at: serverTimestamp()
        },
        { merge: true }
      )
    );
  }

  removeSchemaPermission$(schemaId: string, uid: string): Observable<void> {
    const permissionRef = doc(this.firestore, `schemas/${schemaId}/permissions/${uid}`);
    return from(deleteDoc(permissionRef));
  }

  getAllSchemas$(): Observable<DatasetSchema[]> {
    const schemasRef = collection(this.firestore, 'schemas');
    return from(getDocs(schemasRef)).pipe(
      map((snap) =>
        snap.docs.map((d) => ({
          id: d.id,
          ...(d.data() as Omit<DatasetSchema, 'id'>)
        }))
      )
    );
  }

  async getSchemaAccessByUid(uid: string): Promise<Array<{
    schemaId: string;
    schemaRaw: Record<string, unknown>;
    permissionRaw: Record<string, unknown>;
  }>> {
    const permissionsQuery = query(
      collectionGroup(this.firestore, 'permissions'),
      where('uid', '==', uid)
    );
    const permissionsSnap = await getDocs(permissionsQuery);

    const results: Array<{
      schemaId: string;
      schemaRaw: Record<string, unknown>;
      permissionRaw: Record<string, unknown>;
    }> = [];

    for (const permissionDoc of permissionsSnap.docs) {
      const schemaRef = permissionDoc.ref.parent.parent;
      if (!schemaRef || schemaRef.parent.path !== 'schemas') {
        continue;
      }

      let schemaSnap;
      try {
        schemaSnap = await getDoc(schemaRef);
      } catch {
        continue;
      }

      if (!schemaSnap.exists()) {
        continue;
      }

      results.push({
        schemaId: schemaSnap.id,
        schemaRaw: schemaSnap.data() as Record<string, unknown>,
        permissionRaw: permissionDoc.data() as Record<string, unknown>
      });
    }

    return results;
  }
}
